import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  UtensilsCrossed,
  Users,
  Clock,
  ChefHat,
  CheckCircle2,
  Sparkles,
  Phone,
  Truck,
  Package,
  Flame,
} from "lucide-react";
import { CateringForm } from "../components/CateringForm";

const SERVICES = [
  {
    name: "Drop-Off Catering",
    tagline: "Ready-to-serve, delivered hot",
    description:
      "Perfect for office lunches, small parties, and quick get-togethers. We prep, pack, and deliver — you serve.",
    icon: Package,
    features: [
      "Delivered hot and ready to enjoy",
      "Disposable serving ware included",
      "Ideal for 10 – 50 guests",
      "Simple menu selections",
    ],
    accent: "from-emerald-500 to-emerald-700",
  },
  {
    name: "Buffet Service",
    tagline: "Full spread, chafing dishes, all the sides",
    description:
      "Chef Eric's flavors laid out buffet-style with chafing dishes, real serving utensils, and full setup.",
    icon: Flame,
    features: [
      "Buffet setup with chafing dishes",
      "Multiple entrées and sides",
      "Ideal for 25 – 100+ guests",
      "Perfect for parties, showers, and receptions",
    ],
    accent: "from-amber-500 to-orange-600",
    popular: true,
  },
  {
    name: "Food Truck for Events",
    tagline: "Bring the truck to your event",
    description:
      "Roll the Raffy's Cuisine truck up to your next special event. Weddings, corporate parties, festivals, and reunions.",
    icon: Truck,
    features: [
      "Food truck on-site at your venue",
      "Made-to-order right from the window",
      "Great for weddings and large events",
      "Flat rate + per-guest pricing",
    ],
    accent: "from-stone-700 to-stone-900",
  },
];

export const CateringMenus = () => {
  return (
    <div className="min-h-screen pt-24 pb-0" data-testid="catering-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200 mb-4">
            Catering by Chef Eric
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
            Catering for
            <span className="block text-emerald-600">Every Occasion</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From office lunches to weddings, Chef Eric brings the flavor. Choose the style that
            fits your event — drop-off, buffet, or roll the food truck up to your venue.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
            <span className="flex items-center gap-1.5 text-gray-600">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Chef-prepared
            </span>
            <span className="flex items-center gap-1.5 text-gray-600">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Custom menus
            </span>
            <span className="flex items-center gap-1.5 text-gray-600">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Any group size
            </span>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              How We Cater
            </h2>
            <p className="text-gray-600">Three styles to match any event. Pricing is quoted per event.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((svc) => (
              <Card
                key={svc.name}
                data-testid={`catering-service-${svc.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`relative border-2 ${
                  svc.popular ? "border-emerald-500 shadow-2xl md:scale-[1.02]" : "border-stone-200"
                } overflow-hidden hover:shadow-xl transition-all duration-300`}
              >
                {svc.popular && (
                  <div className="absolute top-0 right-0 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    Most Requested
                  </div>
                )}
                <div className={`h-2 bg-gradient-to-r ${svc.accent}`} />
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-emerald-100 rounded-xl p-3">
                      <svc.icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900">{svc.name}</h3>
                      <p className="text-xs text-emerald-700 font-medium mt-0.5">
                        {svc.tagline}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{svc.description}</p>

                  <ul className="space-y-2 mb-6">
                    {svc.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <a href="#catering-inquiry">
                    <Button
                      className={`w-full ${
                        svc.popular
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-white border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      Request a Quote
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            How Catering Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: "1",
                title: "Tell us about your event",
                text: "Fill out the inquiry form below with your date, guest count, and any dietary requirements.",
                icon: UtensilsCrossed,
              },
              {
                step: "2",
                title: "Chef Eric crafts a menu",
                text: "Within 24 hours, you'll get a custom proposal with menu options, pricing, and timing.",
                icon: ChefHat,
              },
              {
                step: "3",
                title: "Enjoy your event",
                text: "We arrive on time, set up if needed, and make sure everyone eats well.",
                icon: Clock,
              },
            ].map((s) => (
              <div key={s.step} className="relative">
                <Card className="border-2 hover:border-emerald-200 transition-all hover:shadow-md h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-emerald-600 text-white rounded-full h-9 w-9 flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {s.step}
                      </div>
                      <s.icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{s.text}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-600">
              Prefer to talk it through?{" "}
              <a
                href="tel:+16824023734"
                className="text-emerald-700 font-semibold inline-flex items-center gap-1 hover:underline"
              >
                <Phone className="h-4 w-4" /> (682) 402-3734
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Embedded inquiry form */}
      <div id="catering-inquiry" className="scroll-mt-24">
        <CateringForm />
      </div>
    </div>
  );
};
